import { contactModel, ContactRecord } from "@/models/contact";
import { DealRecord, getCurrentDealEntity, getPathDealId } from "@/models/deal";
import { userIsCreatorConstraint } from "@/orm/model";
import { belongsToIds, Entity } from "@/orm/types";
import { Button, SegmentedControl, Stack, TextInput } from "@mantine/core";
import { modals } from "@mantine/modals";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { RiUserAddFill } from "react-icons/ri";
import { stringSimilarity } from "string-similarity-js";
import { EditContactForm } from "../EditContactForm/EditContactForm";

export const openAddContactModal = () => {
  modals.open({
    title: "Add Contact",
    children: <AddContactModal />,
  });
};

const AddContactModal = () => {
  const [value, setValue] = useState("Add New Contact");

  const onSubmitNewContact = async (contact: Partial<ContactRecord>) => {
    const dealId = getPathDealId();

    await contactModel.create({
      ...contact,
      belongsToDealIds: belongsToIds<DealRecord>(dealId ? [dealId] : []),
    });

    modals.closeAll();
  };

  return (
    <Stack>
      <SegmentedControl
        color="blue"
        value={value}
        onChange={setValue}
        data={["Add New Contact", "Add Existing Contact"]}
        styles={{
          control: { borderRadius: 0, width: 160 },
        }}
      />
      {value === "Add New Contact" ? (
        <EditContactForm onSubmit={onSubmitNewContact} />
      ) : (
        <AddExistingContact />
      )}
    </Stack>
  );
};

const AddExistingContact = () => {
  const [search, setSearch] = useState("");

  const { entities } = useAtomValue(
    contactModel.getObserveEntitiesAtom({
      constraints: [userIsCreatorConstraint()],
    }),
  );

  // filter and search contacts name and email, firm with stringSimilarity > 0.5
  // limit to 10 contacts
  const filteredContactRecords = entities
    ?.filter((record) => {
      const contact = record.data!;
      if (
        stringSimilarity(search, contact?.name) > 0.2 ||
        stringSimilarity(search, contact?.email) > 0.2 ||
        stringSimilarity(search, contact?.firm) > 0.2
      )
        return true;

      if (search.length < 3) return false;

      return (
        contact?.name?.toLowerCase().includes(search?.toLowerCase()) ||
        contact?.email?.toLowerCase().includes(search?.toLowerCase()) ||
        contact?.firm?.toLowerCase().includes(search?.toLowerCase())
      );
    })
    .slice(0, 10)
    .sort((a, b) => {
      return (
        stringSimilarity(search, a.data!.name) -
        stringSimilarity(search, b.data!.name)
      );
    });

  const onSelect = async (contactRecord: Entity<ContactRecord>) => {
    contactRecord.createBelongsToRelation<DealRecord>(
      getCurrentDealEntity(),
      "belongsToDealIds",
    );

    modals.closeAll();
  };

  return (
    <Stack gap={"xs"} align="center">
      <TextInput
        label="Search Contact"
        value={search}
        onChange={(e) => {
          setSearch(e.currentTarget.value);
        }}
        w={"100%"}
      />

      {filteredContactRecords?.map((record) => (
        <Button
          key={record.id}
          onClick={() => onSelect(record)}
          w={"100%"}
          variant="outline"
          leftSection={<RiUserAddFill size={20} />}
          justify="flex-start"
        >
          {record.data?.name || "(No name)"}{" "}
          {record.data?.email && `(${record.data?.email})`}{" "}
          {record.data?.firm && `- ${record.data?.firm}`}
        </Button>
      ))}
    </Stack>
  );
};
